import { Component } from '@theme/component';
import { eventBus, BackInStockRequestedEvent, PdpSizeChosenEvent } from '@theme/events';

/**
 * Variant Picker Dropdown — custom dropdown for PDP variant selection.
 *
 * Renders a trigger button that opens a panel with option rows.
 * Keeps hidden radio inputs in sync for pdp--context.js compatibility.
 *
 * Dispatches `PdpSizeChosenEvent` whenever the user picks a size (available or not).
 * The parent `action-product-context` listens to this event to drive its UI state
 * machine (idle / ready / notify). Unavailable rows also open the back-in-stock modal.
 *
 * Starts at the placeholder ("Select size") — a size is only pre-selected when
 * the URL carries a valid ?variant= (deep links: wishlist, shared links).
 * A public `reset()` method restores the placeholder state.
 *
 * @typedef {{ trigger: HTMLButtonElement, triggerText: HTMLSpanElement, triggerStock: HTMLSpanElement, panel: HTMLDivElement }} DropdownRefs
 * @extends {Component<DropdownRefs>}
 */
class VariantPickerDropdown extends Component {
	requiredRefs = ['trigger', 'triggerText', 'triggerStock', 'panel'];

	/** @type {AbortController|null} */
	#outsideClickController = null;

	/** @type {string} Original placeholder text captured on first connect */
	#placeholderText = '';

	connectedCallback() {
		super.connectedCallback();
		if (!this.#placeholderText) {
			// data-placeholder is the reliable source — with a pre-selected variant
			// (deep link) the server renders the trigger already
			// holding the value, so reading textContent would capture the wrong text.
			this.#placeholderText =
				this.dataset.placeholder ?? this.refs.triggerText?.textContent?.trim() ?? '';
		}
		this.#maybePreselectFromUrl();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#removeOutsideListener();
	}

	/**
	 * Toggle panel open/closed — bound via on:click="/toggle" on trigger.
	 */
	toggle() {
		const isOpen = this.refs.trigger.getAttribute('aria-expanded') === 'true';

		if (isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	/**
	 * Close the dropdown panel.
	 */
	close() {
		this.refs.trigger.setAttribute('aria-expanded', 'false');
		this.refs.panel.hidden = true;
		this.#removeOutsideListener();
	}

	/**
	 * Reset the picker back to its initial state: no radio checked, trigger shows
	 * the placeholder, dropdown closed. Called by pdp--context after a successful
	 * cart add.
	 */
	reset() {
		for (const radio of this.querySelectorAll('input[type="radio"]')) {
			radio.checked = false;
		}
		const triggerText = this.querySelector('[ref="triggerText"]');
		const triggerStock = this.querySelector('[ref="triggerStock"]');
		if (triggerText) triggerText.textContent = this.#placeholderText;
		if (triggerStock) triggerStock.textContent = '';
		this.close();
	}

	/**
	 * Public: restore the picker UI to a previously-selected value.
	 * Called by pdp--context after a Section Rendering API morph, since the server
	 * always re-renders the picker with the placeholder (forced-select behavior).
	 * @param {string} value
	 * @param {string} [stockLabel]
	 */
	setSelected(value, stockLabel = '') {
		const radio = /** @type {HTMLInputElement|null} */ (
			this.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`)
		);
		if (radio) radio.checked = true;

		const triggerText = this.querySelector('[ref="triggerText"]');
		const triggerStock = this.querySelector('[ref="triggerStock"]');
		if (triggerText) triggerText.textContent = value;
		if (triggerStock) triggerStock.textContent = stockLabel;
	}

	/**
	 * Pre-select the option value resolved from the URL's ?variant= parameter.
	 * Liquid stamps `data-preselected-value` on this element only when the URL
	 * carried a valid variant (product.selected_variant), so a deep-linked PDP
	 * lands with the size already chosen. This only fills
	 * the trigger UI — no events, no Section Rendering fetch (the server already
	 * rendered this variant); `pdp--context` seeds the matching ATC state
	 * independently from the URL. Unavailable variants render the dimmed
	 * notify trigger.
	 * @returns {boolean} Whether a preselection was applied.
	 */
	#maybePreselectFromUrl() {
		const value = this.dataset.preselectedValue;
		if (!value) return false;

		const row = /** @type {HTMLElement|null} */ (
			this.querySelector(`.product-variant-picker__row[data-value="${CSS.escape(value)}"]`)
		);
		if (!row) return false;

		const stockLabel = row.dataset.stockLabel ?? '';

		if (row.hasAttribute('data-unavailable')) {
			this.#renderNotifyTrigger(value, stockLabel);
			return true;
		}

		const radio = /** @type {HTMLInputElement|null} */ (
			this.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`)
		);
		if (radio) radio.checked = true;

		this.refs.triggerText.textContent = value;
		this.refs.triggerStock.textContent = stockLabel;
		return true;
	}

	/**
	 * Handle option row click — bound via on:click="/selectOption" on each row.
	 * @param {Event} event
	 */
	selectOption(event) {
		const row = /** @type {HTMLElement|null} */ (event.target.closest('[data-value]'));
		if (!row) return;

		const value = row.dataset.value ?? '';
		const variantId = row.dataset.variantId ?? '';
		const stockLabel = row.dataset.stockLabel ?? '';
		const isUnavailable = row.hasAttribute('data-unavailable');

		this.close();

		if (isUnavailable) {
			this.#renderNotifyTrigger(value, stockLabel);
			this.#requestBackInStock(row);

			this.dispatchEvent(
				new PdpSizeChosenEvent({
					data: { variantId, value, available: false, stockLabel },
				})
			);
			return;
		}

		const fieldset = this.querySelector('fieldset[data-option-index]');
		if (!fieldset) return;

		const radio = /** @type {HTMLInputElement|null} */ (
			fieldset.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`)
		);
		if (!radio) return;

		radio.checked = true;

		this.refs.triggerText.textContent = value;
		this.refs.triggerStock.textContent = stockLabel;

		this.dispatchEvent(
			new PdpSizeChosenEvent({
				data: { variantId, value, available: true, stockLabel },
			})
		);

		// Native change event drives pdp--context.js → fetch+morph
		radio.dispatchEvent(new Event('change', { bubbles: true }));
	}

	/**
	 * Render the trigger text for an unavailable variant: the size value is dimmed
	 * and the stock label ("Notify Me") sits next to it.
	 * @param {string} value
	 * @param {string} stockLabel
	 */
	#renderNotifyTrigger(value, stockLabel) {
		const safeValue = value.replace(/[&<>"']/g, (c) => ({
			'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
		}[c]));
		const safeStock = stockLabel.replace(/[&<>"']/g, (c) => ({
			'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
		}[c]));
		this.refs.triggerText.innerHTML =
			`<span class="product-variant-picker__trigger-size--dimmed">${safeValue}</span> ${safeStock}`;
		this.refs.triggerStock.textContent = '';
	}

	/**
	 * Handle keyboard events — bound via on:keydown="/handleKeydown" on the component.
	 * @param {KeyboardEvent} event
	 */
	handleKeydown(event) {
		if (event.key === 'Escape') {
			this.close();
			this.refs.trigger.focus();
		}
	}

	/**
	 * Open the dropdown panel.
	 */
	open() {
		this.refs.trigger.setAttribute('aria-expanded', 'true');
		this.refs.panel.hidden = false;
		this.#addOutsideListener();
	}

	/**
	 * Emit BackInStockRequestedEvent for unavailable variants.
	 * @param {HTMLElement} row
	 */
	#requestBackInStock(row) {
		const context = this.closest('action-product-context');
		if (!context) return;

		const variantId = row.dataset.variantId;

		eventBus.emit(
			new BackInStockRequestedEvent({
				data: {
					productTitle: context.dataset.productTitle ?? '',
					productUrl: context.dataset.productUrl ?? '',
					productImage: context.dataset.productImage ?? '',
					productId: context.dataset.productId ?? '',
					productColor: context.dataset.productColor ?? '',
					variantId: variantId ?? '',
					variantTitle: row.dataset.value ?? '',
					triggerElement: row,
				},
			})
		);
	}

	/**
	 * Add click-outside listener to close dropdown.
	 */
	#addOutsideListener() {
		this.#removeOutsideListener();
		this.#outsideClickController = new AbortController();

		document.addEventListener(
			'click',
			(event) => {
				if (!this.contains(/** @type {Node} */ (event.target))) {
					this.close();
				}
			},
			{ signal: this.#outsideClickController.signal }
		);
	}

	/**
	 * Remove click-outside listener.
	 */
	#removeOutsideListener() {
		this.#outsideClickController?.abort();
		this.#outsideClickController = null;
	}
}

if (!customElements.get('variant-picker-dropdown')) {
	customElements.define('variant-picker-dropdown', VariantPickerDropdown);
}
